import { api } from '../api.js';
import { el, clear, fragment } from '../dom.js';
import { t, formatDate } from '../i18n.js';

// What somebody named you on, and your answer to it.
//
// The list is the invited party's side of an object the other side created,
// which is why it is a page rather than a badge somewhere: an invitation that
// only appears while you happen to be looking at the right event is one nobody
// answers.
//
// Accepted entries stay listed. Leaving later has to be first-class — an
// account must always be able to get an event off its own page — and a list
// that dropped what you agreed to would be the one place that decision is
// unreachable.
export async function renderCollaborations() {
  const list = el('ul', { class: 'card-list' });
  const status = el('p', { class: 'cal-hint', role: 'status' });
  status.hidden = true;

  async function load() {
    const rows = await api.get('/collaborations');
    clear(list);
    if (!rows.length) {
      list.append(el('li', {}, el('div', { class: 'pub-empty' }, t.collaborationsEmpty)));
      return;
    }
    for (const row of rows) list.append(card(row, load, say));
  }

  function say(message) {
    status.textContent = message;
    status.hidden = false;
  }

  await load();

  return fragment([
    el('h1', { class: 'pub-title' }, t.navCollaborations),
    el('p', { class: 'pub-lede' }, t.collaborationsLede),
    status,
    list,
  ]);
}

function card(row, reload, say) {
  const state = row.acceptedAt ? 'accepted' : row.declinedAt ? 'declined' : 'pending';
  const label = {
    accepted: t.collaborationAccepted,
    declined: t.collaborationDeclined,
    pending: t.collaborationPending,
  }[state];

  const answer = async (accepted) => {
    const response = await api.post(
      `/collaborations/${row.seriesId}/${accepted ? 'accept' : 'decline'}`,
      { accountId: row.accountId }
    ).then(() => true).catch(() => false);
    say(response ? t.saved : t.saveFailed);
    if (response) await reload();
  };

  // Accepted entries offer the way out rather than a second yes; declined ones
  // offer the way back in. Neither state is a dead end.
  const actions = state === 'accepted'
    ? [button(t.collaborationLeave, () => answer(false))]
    : [button(t.collaborationAccept, () => answer(true), 'button'),
       state === 'pending' ? button(t.collaborationDecline, () => answer(false)) : null];

  return el('li', {}, el('div', { class: 'card' }, [
    el('div', { class: 'row' }, [
      el('h3', { class: 'card-title' }, row.title),
      el('span', {
        class: `badge ${state === 'accepted' ? 'badge-live' : 'badge-draft'} row-end`,
      }, label),
    ]),
    el('p', { class: 'card-meta' }, [
      formatDate(row.localStartDate),
      row.localStartTime ? ` · ${row.localStartTime}` : '',
      ' · ',
      t.collaborationBy(row.host.name),
      ` · ${row.accountName}`,
    ].join('')),
    el('div', { class: 'row row-tight' }, actions.filter(Boolean)),
  ]));
}

function button(label, onClick, className = 'button button-quiet') {
  const node = el('button', { class: className, type: 'button' }, label);
  node.addEventListener('click', onClick);
  return node;
}
